// Document validation rules (our rules, EFM-agnostic)
import { getMetadataDisplayName } from '../../document-metadata.js';
/**
 * Validate documents.
 *
 * Rules (ours):
 * - At least one document is required
 * - Each document must have a document type selected
 * - When metadata is provided, required metadata fields must be filled in
 */
export function validateDocuments(documents, options) {
    const errors = [];
    const warnings = [];
    const defer = options?.deferPartyAssignment ?? false;
    const deferDocType = options?.deferDocTypeAssignment ?? false;
    const selfRepPartyIds = new Set(options?.selfRepPartyIds ?? []);
    if (documents.length === 0) {
        errors.push({
            field: 'documents',
            rule: 'min-documents',
            message: 'At least one document is required',
            source: 'ours',
        });
        return { valid: false, errors, warnings: [] };
    }
    // Lead document validation. A lead can't be designated until at least one
    // document has a type, so defer this alongside the doc-type requirement.
    const leadDocs = documents.filter(d => d.isLeadDocument);
    if (leadDocs.length === 0) {
        const msg = 'A lead document must be designated';
        if (deferDocType) {
            warnings.push({ field: 'documents', rule: 'lead-document-required', message: msg });
        }
        else {
            errors.push({
                field: 'documents',
                rule: 'lead-document-required',
                message: msg,
                source: 'ours',
            });
        }
    }
    else if (leadDocs.length > 1) {
        errors.push({
            field: 'documents',
            rule: 'single-lead-document',
            message: 'Only one document can be the lead document',
            source: 'ours',
        });
    }
    // formGroup validation (when formGroups data is available).
    // WOL-349: lead formGroup is enforced for case initiation ONLY. Subsequent
    // policy data is unreliable — Madera's subsequent doc types mostly carry
    // EMPTY formGroups (430220 Association, 448140 POS of Summons → []), so
    // requiring EF_LEAD dead-ended every subsequent filing. JTI itself requires
    // exactly one lead document regardless of formGroups.
    const formGroups = options?.formGroupsByDocType;
    const isInitiation = options?.filingType === 'case-initiation';
    if (formGroups && leadDocs.length === 1 && isInitiation) {
        const lead = leadDocs[0];
        if (lead.documentType) {
            const leadFG = formGroups[lead.documentType.code] || [];
            if (!leadFG.includes('EFCI_LEAD')) {
                errors.push({
                    field: 'documents',
                    rule: 'lead-formgroup',
                    message: `Lead document "${lead.name}" is not eligible as a lead document for case initiation`,
                    source: 'ours',
                });
            }
        }
        // Validate non-lead docs have correct formGroup for CI
        for (let i = 0; i < documents.length; i++) {
            const doc = documents[i];
            if (doc.isLeadDocument || !doc.documentType)
                continue;
            const docFG = formGroups[doc.documentType.code] || [];
            if (!docFG.includes('EFCI') && !docFG.includes('EFCI_LEAD')) {
                errors.push({
                    field: `documents[${i}]`,
                    rule: 'doc-formgroup-ci',
                    message: `Document "${doc.name}" cannot be included in a case initiation filing`,
                    source: 'ours',
                });
            }
        }
    }
    for (let i = 0; i < documents.length; i++) {
        const doc = documents[i];
        // Document type is required (deferred on the document-first Documents tab,
        // where the type list isn't available until the case category/type is set).
        if (!doc.documentType) {
            const msg = `"${doc.name}" — select a document type`;
            if (deferDocType) {
                warnings.push({ field: `documents[${i}].documentType`, rule: 'doc-type-required', message: msg });
            }
            else {
                errors.push({
                    field: `documents[${i}].documentType`,
                    rule: 'doc-type-required',
                    message: msg,
                    source: 'ours',
                });
            }
            continue;
        }
        // Validate metadata if metadata items are provided for this doc type
        const metadataItems = options?.metadataByDocType?.[doc.documentType.code];
        if (metadataItems && metadataItems.length > 0) {
            const metaErrors = validateDocumentMetadata(doc, metadataItems, i, defer, selfRepPartyIds);
            errors.push(...metaErrors.errors);
            warnings.push(...metaErrors.warnings);
        }
    }
    return { valid: errors.length === 0, errors, warnings };
}
/**
 * Validate metadata values for a single document against its metadata items.
 */
/**
 * True when a document changes a party's representation at the court — a
 * Substitution of Attorney or Withdrawal of Attorney. Name-based like
 * `isFeeWaiverRequestDoc` (codes are court-scoped; Madera's "Substitution:
 * Attorney" is 467110 civil / 267110 family / 567110 probate, other courts
 * differ). The name is normalized to bare words before matching because live
 * JTI names use a colon, not "of" ("Substitution: Attorney" — the exact name
 * whose non-match made the server backstop reject Walter's standalone
 * Substitution, order fb7fc2bb, 2026-07-03). Association of Attorney
 * deliberately does NOT match — it adds counsel, it never ends of-record
 * representation. (Moved here from ../efiling/validate.ts for WOL-441; still
 * re-exported there.)
 */
export function isRepresentationChangeDoc(documentType) {
    const name = (documentType?.name || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
    return /\b(substitution|withdrawal) (of )?attorneys?\b/.test(name);
}
/**
 * WOL-441 (Walter R92/R93, other-filing-types round): which add-new controls
 * are satisfied by an existing selection. JTI policy metadata is an
 * ALLOW-list, not a require-list (planning/jti/CLAUDE.md), and treating every
 * permitted code as required turned the add-new controls into unsatisfiable
 * dead-ends — a self-rep "Petition: Dissolution" demanded a "New
 * Representation" (policy display name for NEW_ATTORNEY on first-paper docs)
 * with no attorney to add.
 */
const NEW_DATA_SATISFIED_BY = {
    NEW_FILING_PARTY: 'FILING_PARTY',
    NEW_PLAINTIFF: 'FILING_PARTY',
    NEW_RESPONDING_PARTY: 'RESPONDING_PARTY',
    NEW_ATTORNEY: 'FILING_ATTORNEY',
};
/** A non-empty existing-data selection (string[] of roster ids, or legacy bare string). */
function hasSelection(value) {
    return Array.isArray(value) ? value.length > 0 : typeof value === 'string' && value.length > 0;
}
export function validateDocumentMetadata(doc, metadataItems, docIndex, defer = false, selfRepPartyIds) {
    const errors = [];
    const warnings = [];
    const values = doc.metadataValues || {};
    // R18: the document's filing party is self-represented — by doc-level
    // SELF_REP metadata OR by the party flag. This is the SAME predicate the
    // envelope uses to skip its FILING_ATTORNEY arms and emit the self-rep
    // FILING_PARTY_ADDRESS block instead (stage-3-envelope.ts `isSelfRep`), so
    // the validator can never demand an attorney the envelope would not emit.
    const filingParty = values.FILING_PARTY;
    const docSelfRep = values.SELF_REP === true ||
        values.SELF_REP === 'true' ||
        (Array.isArray(filingParty) &&
            filingParty.some(id => typeof id === 'string' && (selfRepPartyIds?.has(id) ?? false)));
    for (const meta of metadataItems) {
        const value = values[meta.code];
        const field = `documents[${docIndex}].metadataValues.${meta.code}`;
        const isPartyField = meta.classType === 'caseParticipant' || meta.classType === 'caseAssignment';
        // R18: a self-rep filing party satisfies FILING_ATTORNEY without an
        // attorney. Keyed on the code, not the caseAssignment class — a
        // substitution's FORMER_ATTORNEY stays required regardless.
        if (meta.code === 'FILING_ATTORNEY' && docSelfRep)
            continue;
        // SOR-235: the address-side complement of the R18 carve-out above. A
        // representation-change document demands the Self Represented Address only
        // when its filing party IS self-represented — the policy merely listing the
        // contact item must not force an attorney-to-attorney substitution to
        // invent one. Keyed on the SAME `docSelfRep` const R18 reads (never
        // re-derived from values.SELF_REP / selfRepPartyIds) so the two carve-outs
        // cannot disagree: the validator never demands an item the envelope builder
        // would drop (stage-3-envelope.ts emits the address only for a self-rep
        // party with a selfRepAddress, or a doc-level contact value).
        //
        // The WOL-463 conjunct keeps the carve-out an allow-list relaxation only:
        // the new-data guard below protects `new-data` items, so a contact item a
        // court actually marks required:true would otherwise be waivable here with
        // nothing else covering it — and that is a field JTI 4082s on at fee calc.
        if (meta.code === 'FILING_PARTY_ADDRESS' &&
            isRepresentationChangeDoc(doc.documentType) &&
            !docSelfRep &&
            !meta.required) {
            continue;
        }
        // R92/R93 (Walter other-filing-types, WOL-441): an add-new control is
        // optional when what it would add already exists on the document —
        // NEW_FILING_PARTY / NEW_PLAINTIFF when the FILING_PARTY selection is
        // satisfied, NEW_RESPONDING_PARTY when RESPONDING_PARTY is, and
        // NEW_ATTORNEY when FILING_ATTORNEY is satisfied OR the filing party is
        // self-represented (R92 — there is no attorney to add; mirrors the R18
        // carve-out above). EXCEPT on representation-change documents
        // (Substitution/Withdrawal of Attorney): naming the incoming attorney is
        // the substance of that filing (SOR-211), so its requirements are
        // untouched — and EXCEPT items the court's policy marks required:true
        // (WOL-463): the counterpart heuristic must never waive a field JTI will
        // 4082 on ("Missing Required Metadata Item" fires at fee calc).
        if (meta.valueRestriction === 'new-data' &&
            !meta.required &&
            !isRepresentationChangeDoc(doc.documentType)) {
            const counterpart = NEW_DATA_SATISFIED_BY[meta.code];
            if (counterpart) {
                const satisfiedBySelfRep = counterpart === 'FILING_ATTORNEY' && docSelfRep;
                if (satisfiedBySelfRep || hasSelection(values[counterpart]))
                    continue;
            }
        }
        if (!hasValue(value, meta)) {
            // SOR-164: the filer-facing display name, not the raw policy name — this
            // message is the fourth surface that shows a metadata name, and the
            // shared override table is what keeps all four reading the same copy.
            const msg = `"${doc.name}" — ${getMetadataDisplayName(meta)} is required`;
            if (defer && isPartyField) {
                warnings.push({ field, rule: `metadata-required-${meta.code}`, message: msg });
            }
            else {
                errors.push({ field, rule: `metadata-required-${meta.code}`, message: msg, source: 'ours' });
            }
        }
    }
    return { errors, warnings };
}
/** Check if a metadata value is meaningfully filled in */
function hasValue(value, meta) {
    // A checkbox has no "unfilled" state — absent means false, which is a
    // legitimate answer (SELF_REP unset = not self-represented). Requiring a
    // touched boolean would block navigation on checkboxes the filer correctly
    // left unchecked.
    if (meta.classType === 'boolean')
        return true;
    if (value === undefined || value === null || value === '')
        return false;
    switch (meta.classType) {
        case 'caseParticipant':
            // Must have at least one party selected (array of UUIDs)
            return Array.isArray(value) && value.length > 0;
        case 'caseAssignment':
            if (meta.valueRestriction === 'new-data') {
                // New attorney: must be a non-empty object
                return typeof value === 'object' && value !== null && Object.keys(value).length > 0;
            }
            // Existing attorney: must be a non-empty string or array
            return Array.isArray(value) ? value.length > 0 : typeof value === 'string' && value.length > 0;
        case 'date':
            return typeof value === 'string' && value.length > 0;
        case 'text':
            return typeof value === 'string' && value.trim().length > 0;
        case 'codeList':
            return typeof value === 'string' && value.length > 0;
        case 'contact':
            // Contact must have at least address fields
            return typeof value === 'object' && value !== null && Object.keys(value).length > 0;
        default:
            return value !== undefined && value !== null;
    }
}
